import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import { useUser } from '@lore/auth';
import ClampableText from '../../components/ClampableText';
import Avatar from '../../components/Avatar';
import logo from '../../../assets/images/logo.png';
import { getDistanceFromLatLonInMiles } from '../../components/User/utils';
import classNames from 'classnames';
import UserOrganization from '../../pages/people/__common/UserOrganization.v2';
import useTags from '../../hooks/useTags';
import TagTypes from '../../constants/TagTypes';
import useOfferTags from '../../hooks/useOfferTags';
import { formatDate, formatDateRange, formatTimeRange } from '../dateTimeUtils';

useOfferEmailPreview.propTypes = {
  offer: PropTypes.object.isRequired
};

export default function useOfferEmailPreview(offer, partner, customMessage) {
  // const { offer } = props;

  const currentUser = useUser();
  const educator = currentUser;
  const distance = getDistanceFromLatLonInMiles(partner.data, currentUser.data);

  const activity = useConnect('tag.byId', {
    id: offer.data.activityId
  });

  const days = useTags(TagTypes.Day);
  const offerDays = useOfferTags(offer, TagTypes.Day);

  const times = useTags(TagTypes.Time);
  const offerTimes = useOfferTags(offer, TagTypes.Time);

  const commitments = useTags(TagTypes.Commitment);
  const offerCommitments = useOfferTags(offer, TagTypes.Commitment);

  const offerInteractionTypes = useOfferTags(offer, TagTypes.InteractionType);

  if (
    activity.state === PayloadStates.FETCHING ||
    days.state === PayloadStates.FETCHING ||
    offerDays.state === PayloadStates.FETCHING ||
    times.state === PayloadStates.FETCHING ||
    offerTimes.state === PayloadStates.FETCHING ||
    commitments.state === PayloadStates.FETCHING ||
    offerCommitments.state === PayloadStates.FETCHING ||
    offerInteractionTypes.state === PayloadStates.FETCHING
  ) {
    return '...';
  }

  function getActivity() {
    return {
      title: `${activity.data.name}`,
      text: `${activity.data.description}`
    };
  }

  function getSummary() {
    return {
      title: `${offer.data.title || activity.data.name}`,
      text: `${offer.data.description || activity.data.description}`
    };
  }

  function getOutcome() {
    if (offer.data.desiredOutcome) {
      return {
        title: `Desired Outcome`,
        text: `${offer.data.desiredOutcome}`
      };
    }
  }

  function getDate() {
    if (
      offer.data.specificDate === 'Yes' &&
      offer.data.date
    ) {
      return {
        title: `Date`,
        text: formatDate(offer.data.date)
      };
    }

    if (
      offer.data.specificDate === 'No' &&
      offer.data.startDate &&
      offer.data.endDate
    ) {
      return {
        title: `Date Window`,
        text: formatDateRange(offer.data.startDate, offer.data.endDate)
      };
    }

    return {
      title: `Date`,
      text: `(not provided)`
    };
  }

  function getDays() {
    if (offerDays.data.length > 0) {
      return {
        title: `Possible Days`,
        text: offerDays.data.map(function(offerDay) {
          return offerDay.data.name;
        }).join('\n')
      };
    }
  }

  function getTime() {
    if (
      offer.data.specificTime === 'Yes' &&
      offer.data.startTime &&
      offer.data.endTime
    ) {
      return {
        title: `Time`,
        text: formatTimeRange(offer.data.startTime, offer.data.endTime, currentUser.data.timezone)
      };
    }

    if (offerTimes.data.length > 0) {
      return {
        title: `Possible Times`,
        text: offerTimes.data.map(function(offerTime) {
          return offerTime.data.name;
        }).join('\n')
      };
    }

    return {
      title: `Time`,
      text: `(not provided)`
    };
  }

  function getCommitment() {
    if (offerCommitments.data.length > 0) {
      return {
        title: `Commitment`,
        text: offerCommitments.data.map(function(offerCommitment) {
          return offerCommitment.data.name;
        }).join('\n')
      };
    }
  }

  function getInPerson() {
    return !!_.find(offerInteractionTypes.data, function(offerInteractionType) {
      return offerInteractionType.data.name === 'In-person';
    });
  }

  function getVirtual() {
    return !!_.find(offerInteractionTypes.data, function(offerInteractionType) {
      return offerInteractionType.data.name === 'Virtual';
    });
  }

  function getCost() {
    return {
      title: `Cost`,
      text: offer.data.hasCost === 'Yes' ?
        offer.data.cost || '(not provided)' :
        'Free'
    };
  }

  const data = {
    activity: getActivity(),
    summary: getSummary(),
    // outcome: getOutcome(),
    date: getDate(),
    // days: getDays(),
    time: getTime(),
    // commitment: getCommitment(),
    inPerson: getInPerson(),
    virtual: getVirtual(),
    cost: getCost()
  }

  return (
    <div className="bg-cs-gray-50 p-6">
      <div className="h-1 bg-cs-orange-500 w-full mb-8"/>
      <div className="p-6 mb-8">
        <img
          className="w-24 mx-auto"
          src={logo}
          alt="logo"
        />
      </div>
      <div className="mb-8">
        <div className="text-base text-cs-gray-900">
          Hi {partner.data.firstName},
        </div>
        <div className="mt-3 text-base text-cs-gray-900">
          <ClampableText markup>
            {customMessage}
          </ClampableText>
        </div>
        <div className="mt-3 mb-3">
          <div className="text-base text-cs-gray-900">
            Cheers,
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div>
            <Avatar size={12} src={currentUser.data.avatarUrl} />
          </div>
          <div>
            <div className="text-lg text-cs-gray-900 font-semibold">
              {currentUser.data.firstName} {currentUser.data.lastName}
            </div>
            <div className="text-sm text-cs-gray-500">
              <UserOrganization user={currentUser} />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex flex-col text-center justify-center bg-cs-orange-500 rounded-xl p-6">
          <div className="text-2xl text-white font-semibold mb-2">
            Are you interested in this Offer?
          </div>
          <div className="text-base text-white mb-4">
            That's wonderful! Connect with {currentUser.data.firstName} to participate, ask
            questions, offer feedback, or just to refer someone you think might be a good fit.
          </div>
          <div className="bg-white rounded-lg w-56 text-center py-3 m-auto">
            <div className="text-base text-cs-orange-500 uppercase font-semibold">
              Respond or Decline
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 space-y-4">
        <div className="text-2xl font-semibold">
          Activity
        </div>
        <div className="bg-white rounded-lg p-6">
          <div className="text-xl font-semibold text-cs-gray-900">
            {data.activity.title}
          </div>
          <div className="text-cs-gray-500">
            {data.activity.text}
          </div>
        </div>
        <div className="text-2xl font-semibold">
          Details
        </div>
        <div className="bg-white rounded-lg p-6 space-y-8">
          <div className="text-2xl font-semibold">
            {data.summary.title}
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              Summary
            </div>
            <div className="text-base text-cs-gray-500">
              <ClampableText markup>
                {data.summary.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              {data.date.title}
            </div>
            <div className="text-cs-gray-500">
              <ClampableText markup>
                {data.date.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              {data.time.title}
            </div>
            <div className="text-cs-gray-500">
              <ClampableText markup>
                {data.time.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              Collaboration Options
            </div>
            {!data.virtual && !data.inPerson && (
              <div>
                <div className="text-cs-gray-900">
                  (not provided)
                </div>
                <div className="text-xs text-cs-gray-500">
                  {distance <= 1 ? '1 mile away' : `${distance} miles away`}
                </div>
              </div>
            )}
            {data.virtual && (
              <div>
                <div className={classNames(
                  'text-cs-gray-900',
                  data.inPerson && 'mb-1'
                )}>
                  Virtual
                </div>
              </div>
            )}
            {data.inPerson && (
              <div>
                <div className="text-cs-gray-900">
                  In-person
                </div>
                <div className="text-xs text-cs-gray-500">
                  {distance <= 1 ? '1 mile away' : `${distance} miles away`}
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="text-xl font-semibold text-cs-gray-900">
              {data.cost.title}
            </div>
            <div className="text-cs-gray-500">
              {data.cost.text}
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex flex-col text-center justify-center bg-cs-orange-500 rounded-xl p-6">
          <div className="text-2xl text-white font-semibold mb-2">
            Are you interested in this Offer?
          </div>
          <div className="text-base text-white mb-4">
            That's wonderful! Connect with {currentUser.data.firstName} to participate, ask
            questions, offer feedback, or just to refer someone you think might be a good fit.
          </div>
          <div className="bg-white rounded-lg w-56 text-center py-3 m-auto">
            <div className="text-base text-cs-orange-500 uppercase font-semibold">
              Respond or Decline
            </div>
          </div>
        </div>
      </div>
      <div className="h-1 bg-cs-orange-500 w-full"/>
    </div>
  );
};
